import React, { useState, useEffect, useMemo } from "react";
import Breadcrumbs from "../../Components/Common/breadcrumbs";
import {
  Box,
  Button,
  Typography,
  Card,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  TablePagination,
  useTheme,
  Avatar,
} from "@mui/material";
import {
  ShieldCheck,
  ShieldAlert,
  Clock,
  CheckCircle2,
  XCircle,
  Search,
  RefreshCw,
  Download,
  ExternalLink,
  Eye,
  Trash2,
  FileText,
  Image as ImageIcon,
  MessageCircle,
  Copy,
  Calendar,
  Mail,
  Phone,
  AlertTriangle,
  FileCheck2,
} from "lucide-react";
import Axios from "../../Common/api/axiosConfig";
import { uploadLink } from "../../utils/constants";
import {
  WarrantyRegistrationRecord,
  getStoredWarrantyRegistrations,
  updateStoredWarrantyRegistrationStatus,
  deleteStoredWarrantyRegistration,
} from "../../utils/warrantyHelper";
import { useNavigate } from "react-router-dom";

const WarrantyRegistrations: React.FC = () => {
  document.title = "Warranty Registrations | Store Admin";

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();

  // Data States
  const [registrations, setRegistrations] = useState<WarrantyRegistrationRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Filter & Search States
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [channelFilter, setChannelFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<"newest" | "oldest">("newest");

  // Pagination States
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  // Modal States
  const [selectedRecord, setSelectedRecord] = useState<WarrantyRegistrationRecord | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [recordToDelete, setRecordToDelete] = useState<WarrantyRegistrationRecord | null>(null);
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Toast / Feedback State
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({
    open: false,
    message: "",
    severity: "info",
  });

  const showToast = (message: string, severity: "success" | "error" | "info" | "warning" = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Fetch registrations from API and combine with local records
  const fetchRegistrations = async (isManualRefresh = false) => {
    if (isManualRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);

    try {
      let apiRecords: WarrantyRegistrationRecord[] = [];

      try {
        const response = await Axios.get("/warranty/registrations");
        if (response.data?.success && Array.isArray(response.data.data)) {
          apiRecords = response.data.data;
        }
      } catch (apiErr: any) {
        console.warn("Could not fetch warranty registrations from backend API:", apiErr?.message);
        // If 404 or backend unavailable, continue to local storage
      }

      // Also get local records stored client-side
      const localRecords = getStoredWarrantyRegistrations();

      // Merge records: API records take precedence, local records fill in any offline/recent items
      const mergedMap = new Map<string, WarrantyRegistrationRecord>();

      // Insert local records first
      localRecords.forEach((item) => {
        const key = item.registrationNumber || item._id;
        mergedMap.set(key, item);
      });

      // Overlay API records
      apiRecords.forEach((item) => {
        const key = item.registrationNumber || item._id;
        mergedMap.set(key, item);
      });

      const combined = Array.from(mergedMap.values()).sort(
        (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
      );

      setRegistrations(combined);
      if (isManualRefresh) {
        showToast(`Refreshed! Found ${combined.length} warranty registration${combined.length === 1 ? "" : "s"}.`, "success");
      }
    } catch (err: any) {
      console.error("Error loading warranty registrations:", err);
      setError("Failed to load warranty registrations. Please try again.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchRegistrations();
  }, []);

  // Update Status handler
  const handleStatusChange = async (
    record: WarrantyRegistrationRecord,
    newStatus: "pending" | "verified" | "rejected"
  ) => {
    if (record.status === newStatus) return;
    setUpdatingStatusId(record._id);

    // Optimistic UI update
    setRegistrations((prev) =>
      prev.map((r) =>
        r._id === record._id || r.registrationNumber === record.registrationNumber
          ? { ...r, status: newStatus, updatedAt: new Date().toISOString() }
          : r
      )
    );

    if (selectedRecord && (selectedRecord._id === record._id || selectedRecord.registrationNumber === record.registrationNumber)) {
      setSelectedRecord((prev) => (prev ? { ...prev, status: newStatus } : null));
    }

    // Persist locally
    updateStoredWarrantyRegistrationStatus(record.registrationNumber || record._id, newStatus);

    // Persist to backend if record has MongoDB ID
    const isMongoId = /^[0-9a-fA-F]{24}$/.test(record._id);
    if (isMongoId) {
      try {
        await Axios.patch(`/warranty/registrations/${record._id}/status`, {
          status: newStatus,
        });
        showToast(`Registration ${record.registrationNumber || record.orderId} marked as ${newStatus}.`, "success");
      } catch (err: any) {
        console.error("Backend status update error:", err);
        showToast(`Status updated locally to ${newStatus}.`, "info");
      }
    } else {
      showToast(`Status updated to ${newStatus}.`, "success");
    }

    setUpdatingStatusId(null);
  };

  // Delete Record handler
  const handleDeleteRecord = async () => {
    if (!recordToDelete) return;
    const target = recordToDelete;
    setDeletingId(target._id);

    try {
      // Remove from backend if Mongo ID
      const isMongoId = /^[0-9a-fA-F]{24}$/.test(target._id);
      if (isMongoId) {
        try {
          await Axios.delete(`/warranty/registrations/${target._id}`);
        } catch (err) {
          console.warn("Backend delete error:", err);
        }
      }

      // Remove from local storage
      deleteStoredWarrantyRegistration(target.registrationNumber || target._id);

      // Remove from state
      setRegistrations((prev) =>
        prev.filter((r) => r._id !== target._id && r.registrationNumber !== target.registrationNumber)
      );

      showToast(`Warranty registration ${target.registrationNumber || target.orderId} deleted.`, "success");
      setShowDeleteModal(false);
      if (selectedRecord?._id === target._id) {
        setShowDetailsModal(false);
      }
    } catch (err: any) {
      console.error("Delete error:", err);
      showToast("Could not delete registration.", "error");
    } finally {
      setDeletingId(null);
      setRecordToDelete(null);
    }
  };

  // Copy helper
  const copyToClipboard = (text: string, label: string) => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
      showToast(`${label} copied to clipboard!`, "info");
    }
  };

  // Open invoice link
  const getInvoiceFullUrl = (invoiceUrl: string): string => {
    if (!invoiceUrl) return "";
    if (invoiceUrl.startsWith("http") || invoiceUrl.startsWith("blob:") || invoiceUrl.startsWith("data:")) {
      return invoiceUrl;
    }
    const cleanPath = invoiceUrl.replace(/^\/+/, "");
    return `${uploadLink}${cleanPath}`;
  };

  // CSV Export
  const handleExportCSV = () => {
    if (filteredRegistrations.length === 0) {
      showToast("No records to export.", "warning");
      return;
    }

    const headers = [
      "Registration ID",
      "Customer Name",
      "Email",
      "Email Opt-in",
      "Mobile Number",
      "Product Name",
      "Order ID",
      "Date of Purchase",
      "Purchased From",
      "Status",
      "Registered At",
      "Invoice URL",
    ];

    const rows = filteredRegistrations.map((r) => [
      `"${r.registrationNumber || ""}"`,
      `"${(r.customerName || "").replace(/"/g, '""')}"`,
      `"${r.email || ""}"`,
      r.emailNotifications ? "Yes" : "No",
      `"${r.mobileNumber || ""}"`,
      `"${(r.productName || "").replace(/"/g, '""')}"`,
      `"${(r.orderId || "").replace(/"/g, '""')}"`,
      `"${r.dateOfPurchase ? new Date(r.dateOfPurchase).toLocaleDateString("en-IN") : ""}"`,
      `"${(r.purchasedFrom || "").replace(/"/g, '""')}"`,
      r.status || "pending",
      `"${r.createdAt ? new Date(r.createdAt).toLocaleString("en-IN") : ""}"`,
      `"${getInvoiceFullUrl(r.invoiceUrl)}"`,
    ]);

    const csvContent = [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `warranty_registrations_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast("CSV exported successfully!", "success");
  };

  // KPI Statistics
  const stats = useMemo(() => {
    const total = registrations.length;
    const pending = registrations.filter((r) => (r.status || "pending").toLowerCase() === "pending").length;
    const verified = registrations.filter((r) => (r.status || "").toLowerCase() === "verified").length;
    const rejected = registrations.filter((r) => (r.status || "").toLowerCase() === "rejected").length;
    return { total, pending, verified, rejected };
  }, [registrations]);

  // Unique purchase channels for dropdown
  const channels = useMemo(() => {
    const set = new Set<string>();
    registrations.forEach((r) => {
      if (r.purchasedFrom) set.add(r.purchasedFrom.trim());
    });
    return Array.from(set);
  }, [registrations]);

  // Filtered & Sorted Registrations
  const filteredRegistrations = useMemo(() => {
    return registrations
      .filter((record) => {
        // Status filter
        if (statusFilter !== "all" && (record.status || "pending").toLowerCase() !== statusFilter.toLowerCase()) {
          return false;
        }

        // Channel filter
        if (channelFilter !== "all" && record.purchasedFrom !== channelFilter) {
          return false;
        }

        // Search filter
        if (searchTerm.trim()) {
          const q = searchTerm.toLowerCase().trim();
          const matchesReg = (record.registrationNumber || "").toLowerCase().includes(q);
          const matchesName = (record.customerName || "").toLowerCase().includes(q);
          const matchesEmail = (record.email || "").toLowerCase().includes(q);
          const matchesMobile = (record.mobileNumber || "").toLowerCase().includes(q);
          const matchesProduct = (record.productName || "").toLowerCase().includes(q);
          const matchesOrder = (record.orderId || "").toLowerCase().includes(q);
          return matchesReg || matchesName || matchesEmail || matchesMobile || matchesProduct || matchesOrder;
        }

        return true;
      })
      .sort((a, b) => {
        const timeA = new Date(a.createdAt || 0).getTime();
        const timeB = new Date(b.createdAt || 0).getTime();
        return sortBy === "newest" ? timeB - timeA : timeA - timeB;
      });
  }, [registrations, statusFilter, channelFilter, searchTerm, sortBy]);

  // Paginated records
  const paginatedRegistrations = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRegistrations.slice(start, start + rowsPerPage);
  }, [filteredRegistrations, page, rowsPerPage]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Status Chip Component
  const renderStatusChip = (status: string, size: "small" | "medium" = "small") => {
    const s = (status || "pending").toLowerCase();
    if (s === "verified") {
      return (
        <Chip
          icon={<CheckCircle2 size={14} style={{ color: "#16a34a" }} />}
          label="Verified"
          size={size}
          sx={{
            fontWeight: 700,
            fontSize: size === "small" ? "0.75rem" : "0.85rem",
            bgcolor: isDark ? "rgba(22,163,74,0.15)" : "#dcfce7",
            color: isDark ? "#4ade80" : "#166534",
            border: `1px solid ${isDark ? "rgba(74,222,128,0.3)" : "#bbf7d0"}`,
          }}
        />
      );
    }
    if (s === "rejected") {
      return (
        <Chip
          icon={<XCircle size={14} style={{ color: "#dc2626" }} />}
          label="Rejected"
          size={size}
          sx={{
            fontWeight: 700,
            fontSize: size === "small" ? "0.75rem" : "0.85rem",
            bgcolor: isDark ? "rgba(220,38,38,0.15)" : "#fee2e2",
            color: isDark ? "#f87171" : "#991b1b",
            border: `1px solid ${isDark ? "rgba(248,113,113,0.3)" : "#fecaca"}`,
          }}
        />
      );
    }
    return (
      <Chip
        icon={<Clock size={14} style={{ color: "#d97706" }} />}
        label="Pending"
        size={size}
        sx={{
          fontWeight: 700,
          fontSize: size === "small" ? "0.75rem" : "0.85rem",
          bgcolor: isDark ? "rgba(217,119,6,0.15)" : "#fef3c7",
          color: isDark ? "#fbbf24" : "#92400e",
          border: `1px solid ${isDark ? "rgba(251,191,36,0.3)" : "#fde68a"}`,
        }}
      />
    );
  };

  return (
    <Box sx={{ pb: 6 }}>
      {/* Top Store Banner */}
      <Breadcrumbs />

      {error && (
        <Alert severity="error" sx={{ mt: 2.5, borderRadius: 2.5 }}>
          {error}
        </Alert>
      )}

      {/* Page Title & Actions Bar */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          gap: 2,
          mt: 3,
          mb: 3,
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2.5,
                bgcolor: "#2563eb",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                boxShadow: "0 4px 12px rgba(37,99,235,0.25)",
              }}
            >
              <ShieldCheck size={26} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: isDark ? "#fff" : "#0f172a" }}>
                Warranty Registrations
              </Typography>
              <Typography variant="body2" sx={{ color: isDark ? "#94a3b8" : "#64748b" }}>
                Review customer registrations, verify proof-of-purchase invoices, and manage warranty coverage
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignSelf: { xs: "stretch", sm: "auto" } }}>
          <Button
            variant="outlined"
            startIcon={<RefreshCw size={16} className={refreshing ? "spin-icon" : ""} />}
            onClick={() => fetchRegistrations(true)}
            disabled={loading || refreshing}
            sx={{
              borderRadius: 2.5,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.875rem",
              borderColor: isDark ? "#334155" : "#cbd5e1",
              color: isDark ? "#cbd5e1" : "#475569",
              "&:hover": {
                borderColor: "#2563eb",
                color: "#2563eb",
                bgcolor: "rgba(37,99,235,0.04)",
              },
            }}
          >
            {refreshing ? "Refreshing..." : "Refresh"}
          </Button>

          <Button
            variant="outlined"
            startIcon={<Download size={16} />}
            onClick={handleExportCSV}
            disabled={filteredRegistrations.length === 0}
            sx={{
              borderRadius: 2.5,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.875rem",
              borderColor: isDark ? "#334155" : "#cbd5e1",
              color: isDark ? "#cbd5e1" : "#475569",
              "&:hover": {
                borderColor: "#16a34a",
                color: "#16a34a",
                bgcolor: "rgba(22,163,74,0.04)",
              },
            }}
          >
            Export CSV
          </Button>

          <Button
            variant="contained"
            startIcon={<FileCheck2 size={16} />}
            onClick={() => navigate("/manage-products")}
            sx={{
              borderRadius: 2.5,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.875rem",
              background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
              boxShadow: "0 4px 10px rgba(37,99,235,0.3)",
              color: "#fff",
            }}
          >
            Manage Products Warranty
          </Button>
        </Box>
      </Box>

      {/* KPI Stat Cards */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {/* Total Registrations */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              p: 2.2,
              borderRadius: 3,
              border: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
              bgcolor: isDark ? "#0f172a" : "#ffffff",
              boxShadow: isDark ? "0 2px 8px rgba(0,0,0,0.4)" : "0 2px 8px rgba(0,0,0,0.04)",
              cursor: "pointer",
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 6px 16px rgba(37,99,235,0.12)",
                borderColor: "#2563eb",
              },
            }}
            onClick={() => setStatusFilter("all")}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box>
                <Typography variant="body2" sx={{ color: isDark ? "#94a3b8" : "#64748b", fontWeight: 600 }}>
                  Total Registrations
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5, color: isDark ? "#fff" : "#0f172a" }}>
                  {stats.total}
                </Typography>
                <Typography variant="caption" sx={{ color: isDark ? "#64748b" : "#94a3b8" }}>
                  All customer claims
                </Typography>
              </Box>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 3,
                  bgcolor: isDark ? "rgba(37,99,235,0.15)" : "#eff6ff",
                  color: "#2563eb",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FileText size={24} />
              </Box>
            </Box>
          </Card>
        </Grid>

        {/* Pending Verification */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              p: 2.2,
              borderRadius: 3,
              border: `1px solid ${statusFilter === "pending" ? "#f59e0b" : isDark ? "#1e293b" : "#e2e8f0"}`,
              bgcolor: isDark ? "#0f172a" : "#ffffff",
              boxShadow: isDark ? "0 2px 8px rgba(0,0,0,0.4)" : "0 2px 8px rgba(0,0,0,0.04)",
              cursor: "pointer",
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 6px 16px rgba(245,158,11,0.15)",
                borderColor: "#f59e0b",
              },
            }}
            onClick={() => setStatusFilter("pending")}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box>
                <Typography variant="body2" sx={{ color: isDark ? "#94a3b8" : "#64748b", fontWeight: 600 }}>
                  Pending Verification
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5, color: "#f59e0b" }}>
                  {stats.pending}
                </Typography>
                <Typography variant="caption" sx={{ color: isDark ? "#64748b" : "#94a3b8" }}>
                  Requires review
                </Typography>
              </Box>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 3,
                  bgcolor: isDark ? "rgba(245,158,11,0.15)" : "#fffbeb",
                  color: "#f59e0b",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Clock size={24} />
              </Box>
            </Box>
          </Card>
        </Grid>

        {/* Verified */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              p: 2.2,
              borderRadius: 3,
              border: `1px solid ${statusFilter === "verified" ? "#16a34a" : isDark ? "#1e293b" : "#e2e8f0"}`,
              bgcolor: isDark ? "#0f172a" : "#ffffff",
              boxShadow: isDark ? "0 2px 8px rgba(0,0,0,0.4)" : "0 2px 8px rgba(0,0,0,0.04)",
              cursor: "pointer",
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 6px 16px rgba(22,163,74,0.15)",
                borderColor: "#16a34a",
              },
            }}
            onClick={() => setStatusFilter("verified")}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box>
                <Typography variant="body2" sx={{ color: isDark ? "#94a3b8" : "#64748b", fontWeight: 600 }}>
                  Verified
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5, color: "#16a34a" }}>
                  {stats.verified}
                </Typography>
                <Typography variant="caption" sx={{ color: isDark ? "#64748b" : "#94a3b8" }}>
                  Active coverage
                </Typography>
              </Box>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 3,
                  bgcolor: isDark ? "rgba(22,163,74,0.15)" : "#f0fdf4",
                  color: "#16a34a",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CheckCircle2 size={24} />
              </Box>
            </Box>
          </Card>
        </Grid>

        {/* Rejected */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              p: 2.2,
              borderRadius: 3,
              border: `1px solid ${statusFilter === "rejected" ? "#dc2626" : isDark ? "#1e293b" : "#e2e8f0"}`,
              bgcolor: isDark ? "#0f172a" : "#ffffff",
              boxShadow: isDark ? "0 2px 8px rgba(0,0,0,0.4)" : "0 2px 8px rgba(0,0,0,0.04)",
              cursor: "pointer",
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 6px 16px rgba(220,38,38,0.15)",
                borderColor: "#dc2626",
              },
            }}
            onClick={() => setStatusFilter("rejected")}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box>
                <Typography variant="body2" sx={{ color: isDark ? "#94a3b8" : "#64748b", fontWeight: 600 }}>
                  Rejected
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5, color: "#dc2626" }}>
                  {stats.rejected}
                </Typography>
                <Typography variant="caption" sx={{ color: isDark ? "#64748b" : "#94a3b8" }}>
                  Invalid / duplicate
                </Typography>
              </Box>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 3,
                  bgcolor: isDark ? "rgba(220,38,38,0.15)" : "#fef2f2",
                  color: "#dc2626",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <XCircle size={24} />
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Filter & Search Toolbar */}
      <Card
        sx={{
          p: 2.5,
          borderRadius: 3,
          border: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
          bgcolor: isDark ? "#0f172a" : "#ffffff",
          mb: 3,
          boxShadow: isDark ? "0 2px 8px rgba(0,0,0,0.4)" : "0 2px 8px rgba(0,0,0,0.04)",
        }}
      >
        <Grid container spacing={2} alignItems="center">
          {/* Search Input */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by customer, email, order ID, or product..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} color="#94a3b8" />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 2.5,
                  fontSize: "0.875rem",
                  bgcolor: isDark ? "#1e293b" : "#f8fafc",
                },
              }}
            />
          </Grid>

          {/* Status Filter */}
          <Grid item xs={6} sm={4} md={2.5}>
            <FormControl fullWidth size="small">
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
                sx={{
                  borderRadius: 2.5,
                  fontSize: "0.875rem",
                  bgcolor: isDark ? "#1e293b" : "#f8fafc",
                }}
              >
                <MenuItem value="all">Status: All ({stats.total})</MenuItem>
                <MenuItem value="pending">Pending ({stats.pending})</MenuItem>
                <MenuItem value="verified">Verified ({stats.verified})</MenuItem>
                <MenuItem value="rejected">Rejected ({stats.rejected})</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Channel Filter */}
          <Grid item xs={6} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <Select
                value={channelFilter}
                onChange={(e) => {
                  setChannelFilter(e.target.value);
                  setPage(0);
                }}
                sx={{
                  borderRadius: 2.5,
                  fontSize: "0.875rem",
                  bgcolor: isDark ? "#1e293b" : "#f8fafc",
                }}
              >
                <MenuItem value="all">Channel: All Channels</MenuItem>
                {channels.map((ch) => (
                  <MenuItem key={ch} value={ch}>
                    {ch}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Sort By */}
          <Grid item xs={12} sm={4} md={2.5}>
            <FormControl fullWidth size="small">
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as "newest" | "oldest")}
                sx={{
                  borderRadius: 2.5,
                  fontSize: "0.875rem",
                  bgcolor: isDark ? "#1e293b" : "#f8fafc",
                }}
              >
                <MenuItem value="newest">Sort: Newest First</MenuItem>
                <MenuItem value="oldest">Sort: Oldest First</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Card>

      {/* Main Registrations Table */}
      <Card
        sx={{
          borderRadius: 3,
          border: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
          bgcolor: isDark ? "#0f172a" : "#ffffff",
          boxShadow: isDark ? "0 2px 8px rgba(0,0,0,0.4)" : "0 2px 8px rgba(0,0,0,0.04)",
          overflow: "hidden",
        }}
      >
        {loading ? (
          <Box sx={{ p: 6, textAlign: "center" }}>
            <CircularProgress size={36} sx={{ color: "#2563eb", mb: 2 }} />
            <Typography variant="body2" sx={{ color: isDark ? "#94a3b8" : "#64748b" }}>
              Loading warranty registrations...
            </Typography>
          </Box>
        ) : filteredRegistrations.length === 0 ? (
          <Box sx={{ p: 6, textAlign: "center" }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 4,
                bgcolor: isDark ? "#1e293b" : "#f1f5f9",
                color: isDark ? "#94a3b8" : "#64748b",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <ShieldAlert size={32} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: isDark ? "#fff" : "#0f172a" }}>
              No Warranty Registrations Found
            </Typography>
            <Typography variant="body2" sx={{ color: isDark ? "#94a3b8" : "#64748b", maxWidth: 440, mx: "auto", mb: 2.5 }}>
              {searchTerm || statusFilter !== "all" || channelFilter !== "all"
                ? "No registrations match your current filter criteria. Try clearing filters or changing search keywords."
                : "When customers register their warranties on your storefront, they will automatically appear in this table for verification."}
            </Typography>
            {(searchTerm || statusFilter !== "all" || channelFilter !== "all") && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setSearchTerm("");
                  setStatusFilter("all");
                  setChannelFilter("all");
                }}
                sx={{ borderRadius: 2, textTransform: "none" }}
              >
                Clear Filters
              </Button>
            )}
          </Box>
        ) : (
          <>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 900 }}>
                <TableHead sx={{ bgcolor: isDark ? "#1e293b" : "#f8fafc" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.8rem", color: isDark ? "#cbd5e1" : "#475569" }}>
                      REGISTRATION ID
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.8rem", color: isDark ? "#cbd5e1" : "#475569" }}>
                      CUSTOMER
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.8rem", color: isDark ? "#cbd5e1" : "#475569" }}>
                      PRODUCT & ORDER
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.8rem", color: isDark ? "#cbd5e1" : "#475569" }}>
                      PURCHASE DETAILS
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.8rem", color: isDark ? "#cbd5e1" : "#475569" }}>
                      INVOICE / PROOF
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.8rem", color: isDark ? "#cbd5e1" : "#475569" }}>
                      STATUS
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.8rem", color: isDark ? "#cbd5e1" : "#475569" }}>
                      ACTIONS
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedRegistrations.map((row) => {
                    const isUpdating = updatingStatusId === row._id;
                    const invoiceLink = getInvoiceFullUrl(row.invoiceUrl);
                    const isPdf =
                      row.invoiceMimeType === "application/pdf" ||
                      row.invoiceUrl?.toLowerCase().endsWith(".pdf") ||
                      row.invoiceOriginalName?.toLowerCase().endsWith(".pdf");

                    return (
                      <TableRow
                        key={row._id || row.registrationNumber}
                        hover
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                          transition: "background-color 0.15s",
                        }}
                      >
                        {/* Registration ID & Date */}
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                fontFamily: "monospace",
                                color: "#2563eb",
                                bgcolor: isDark ? "rgba(37,99,235,0.12)" : "#eff6ff",
                                px: 1,
                                py: 0.3,
                                borderRadius: 1.5,
                                fontSize: "0.825rem",
                              }}
                            >
                              {row.registrationNumber || "WR-PENDING"}
                            </Typography>
                            {row.registrationNumber && (
                              <Tooltip title="Copy Registration ID">
                                <IconButton
                                  size="small"
                                  onClick={() => copyToClipboard(row.registrationNumber, "Registration ID")}
                                  sx={{ p: 0.5, color: "#94a3b8", "&:hover": { color: "#2563eb" } }}
                                >
                                  <Copy size={13} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                          <Typography variant="caption" sx={{ color: isDark ? "#64748b" : "#94a3b8", display: "block", mt: 0.5 }}>
                            {row.createdAt
                              ? new Date(row.createdAt).toLocaleDateString("en-IN", {
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })
                              : "Just now"}
                          </Typography>
                        </TableCell>

                        {/* Customer Info */}
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                            <Avatar
                              sx={{
                                width: 34,
                                height: 34,
                                fontSize: "0.85rem",
                                fontWeight: 700,
                                bgcolor: "#3b82f6",
                              }}
                            >
                              {row.customerName ? row.customerName[0].toUpperCase() : "C"}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: isDark ? "#fff" : "#0f172a" }}>
                                {row.customerName}
                              </Typography>

                              {/* Email */}
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.2 }}>
                                <Mail size={12} color="#94a3b8" />
                                <Typography
                                  component="a"
                                  href={`mailto:${row.email}`}
                                  variant="caption"
                                  sx={{
                                    color: isDark ? "#94a3b8" : "#64748b",
                                    textDecoration: "none",
                                    "&:hover": { color: "#2563eb", textDecoration: "underline" },
                                  }}
                                >
                                  {row.email}
                                </Typography>
                              </Box>

                              {/* Mobile + WhatsApp */}
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mt: 0.2 }}>
                                <Phone size={12} color="#94a3b8" />
                                <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b" }}>
                                  {row.mobileNumber}
                                </Typography>
                                {row.mobileNumber && (
                                  <Tooltip title="Chat on WhatsApp">
                                    <IconButton
                                      size="small"
                                      component="a"
                                      href={`https://wa.me/${row.mobileNumber.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(
                                        `Hello ${row.customerName}, regarding your warranty registration ${row.registrationNumber || ""} for ${row.productName}...`
                                      )}`}
                                      target="_blank"
                                      sx={{ p: 0.2, color: "#22c55e", "&:hover": { bgcolor: "rgba(34,197,94,0.1)" } }}
                                    >
                                      <MessageCircle size={13} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </TableCell>

                        {/* Product & Order */}
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: isDark ? "#fff" : "#0f172a" }}>
                            {row.productName}
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.4 }}>
                            <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b" }}>
                              Order ID:
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 700,
                                fontFamily: "monospace",
                                color: isDark ? "#e2e8f0" : "#1e293b",
                              }}
                            >
                              {row.orderId}
                            </Typography>
                            <Tooltip title="Copy Order ID">
                              <IconButton
                                size="small"
                                onClick={() => copyToClipboard(row.orderId, "Order ID")}
                                sx={{ p: 0.2, color: "#94a3b8", "&:hover": { color: "#2563eb" } }}
                              >
                                <Copy size={11} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>

                        {/* Purchase Details */}
                        <TableCell>
                          <Chip
                            label={row.purchasedFrom || "Store"}
                            size="small"
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              bgcolor: isDark ? "#1e293b" : "#f1f5f9",
                              color: isDark ? "#cbd5e1" : "#475569",
                              mb: 0.5,
                              maxWidth: 160,
                            }}
                          />
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Calendar size={12} color="#94a3b8" />
                            <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b" }}>
                              {row.dateOfPurchase
                                ? new Date(row.dateOfPurchase).toLocaleDateString("en-IN", {
                                    day: "numeric",
                                    month: "short",
                                    year: "numeric",
                                  })
                                : "N/A"}
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* Invoice Proof */}
                        <TableCell>
                          {row.invoiceUrl ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={isPdf ? <FileText size={14} color="#ef4444" /> : <ImageIcon size={14} color="#3b82f6" />}
                                onClick={() => {
                                  setSelectedRecord(row);
                                  setShowDetailsModal(true);
                                }}
                                sx={{
                                  borderRadius: 2,
                                  textTransform: "none",
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                  px: 1.2,
                                  py: 0.4,
                                  borderColor: isDark ? "#334155" : "#e2e8f0",
                                }}
                              >
                                {isPdf ? "PDF Invoice" : "Image Proof"}
                              </Button>
                              <Tooltip title="Open Original File">
                                <IconButton
                                  size="small"
                                  component="a"
                                  href={invoiceLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{ color: "#94a3b8", "&:hover": { color: "#2563eb" } }}
                                >
                                  <ExternalLink size={14} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : (
                            <Typography variant="caption" sx={{ color: "#94a3b8", fontStyle: "italic" }}>
                              No file attached
                            </Typography>
                          )}
                        </TableCell>

                        {/* Status with Quick Toggle */}
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {isUpdating ? (
                              <CircularProgress size={16} sx={{ color: "#2563eb" }} />
                            ) : (
                              <FormControl size="small" sx={{ minWidth: 110 }}>
                                <Select
                                  value={(row.status || "pending").toLowerCase()}
                                  onChange={(e) =>
                                    handleStatusChange(row, e.target.value as "pending" | "verified" | "rejected")
                                  }
                                  sx={{
                                    height: 30,
                                    fontSize: "0.75rem",
                                    fontWeight: 700,
                                    borderRadius: 2,
                                    bgcolor:
                                      (row.status || "pending").toLowerCase() === "verified"
                                        ? isDark
                                          ? "rgba(22,163,74,0.15)"
                                          : "#dcfce7"
                                        : (row.status || "pending").toLowerCase() === "rejected"
                                        ? isDark
                                          ? "rgba(220,38,38,0.15)"
                                          : "#fee2e2"
                                        : isDark
                                        ? "rgba(217,119,6,0.15)"
                                        : "#fef3c7",
                                    color:
                                      (row.status || "pending").toLowerCase() === "verified"
                                        ? isDark
                                          ? "#4ade80"
                                          : "#166534"
                                        : (row.status || "pending").toLowerCase() === "rejected"
                                        ? isDark
                                          ? "#f87171"
                                          : "#991b1b"
                                        : isDark
                                        ? "#fbbf24"
                                        : "#92400e",
                                    "& fieldset": {
                                      borderColor:
                                        (row.status || "pending").toLowerCase() === "verified"
                                          ? "#86efac"
                                          : (row.status || "pending").toLowerCase() === "rejected"
                                          ? "#fca5a5"
                                          : "#fde047",
                                    },
                                  }}
                                >
                                  <MenuItem value="pending" sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
                                    Pending
                                  </MenuItem>
                                  <MenuItem value="verified" sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#16a34a" }}>
                                    Verified
                                  </MenuItem>
                                  <MenuItem value="rejected" sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#dc2626" }}>
                                    Rejected
                                  </MenuItem>
                                </Select>
                              </FormControl>
                            )}
                          </Box>
                        </TableCell>

                        {/* Actions */}
                        <TableCell align="right">
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
                            <Tooltip title="View Full Details & Invoice">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedRecord(row);
                                  setShowDetailsModal(true);
                                }}
                                sx={{ color: "#3b82f6", "&:hover": { bgcolor: "rgba(59,130,246,0.1)" } }}
                              >
                                <Eye size={16} />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Delete Registration">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setRecordToDelete(row);
                                  setShowDeleteModal(true);
                                }}
                                sx={{ color: "#ef4444", "&:hover": { bgcolor: "rgba(239,68,68,0.1)" } }}
                              >
                                <Trash2 size={16} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination Controls */}
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredRegistrations.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                borderTop: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
                color: isDark ? "#cbd5e1" : "#475569",
              }}
            />
          </>
        )}
      </Card>

      {/* Details & Invoice Viewer Modal */}
      <Dialog
        open={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3.5,
            bgcolor: isDark ? "#0f172a" : "#ffffff",
            backgroundImage: "none",
          },
        }}
      >
        {selectedRecord && (
          <>
            <DialogTitle
              sx={{
                p: 2.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2.5,
                    bgcolor: "#2563eb",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ShieldCheck size={22} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    Warranty Registration Details
                  </Typography>
                  <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b" }}>
                    ID: {selectedRecord.registrationNumber || "WR-PENDING"}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {renderStatusChip(selectedRecord.status, "medium")}
              </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
              <Grid container spacing={3}>
                {/* Left: Customer & Purchase Details */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5, color: isDark ? "#cbd5e1" : "#334155" }}>
                    Customer Information
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2.5,
                      bgcolor: isDark ? "#1e293b" : "#f8fafc",
                      border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
                      mb: 2.5,
                    }}
                  >
                    <Box sx={{ mb: 1.2 }}>
                      <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b" }}>
                        Full Name
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {selectedRecord.customerName}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 1.2 }}>
                      <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b" }}>
                        Email Address
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        <a href={`mailto:${selectedRecord.email}`} style={{ color: "#2563eb", textDecoration: "none" }}>
                          {selectedRecord.email}
                        </a>
                      </Typography>
                      <Chip
                        label={selectedRecord.emailNotifications ? "✓ Email Notifications Opted In" : "Notifications Opted Out"}
                        size="small"
                        sx={{
                          mt: 0.5,
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          bgcolor: selectedRecord.emailNotifications ? (isDark ? "rgba(22,163,74,0.15)" : "#dcfce7") : "#f1f5f9",
                          color: selectedRecord.emailNotifications ? "#16a34a" : "#64748b",
                        }}
                      />
                    </Box>

                    <Box>
                      <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b" }}>
                        Mobile Number
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {selectedRecord.mobileNumber}
                        </Typography>
                        {selectedRecord.mobileNumber && (
                          <Button
                            variant="outlined"
                            size="small"
                            component="a"
                            href={`https://wa.me/${selectedRecord.mobileNumber.replace(/[^0-9]/g, "")}`}
                            target="_blank"
                            startIcon={<MessageCircle size={14} color="#22c55e" />}
                            sx={{
                              borderRadius: 2,
                              py: 0.2,
                              px: 1,
                              fontSize: "0.72rem",
                              textTransform: "none",
                              color: "#22c55e",
                              borderColor: "#22c55e",
                            }}
                          >
                            WhatsApp
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5, color: isDark ? "#cbd5e1" : "#334155" }}>
                    Purchase Details
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2.5,
                      bgcolor: isDark ? "#1e293b" : "#f8fafc",
                      border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
                    }}
                  >
                    <Box sx={{ mb: 1.2 }}>
                      <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b" }}>
                        Product Name
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: "#2563eb" }}>
                        {selectedRecord.productName}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 1.2 }}>
                      <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b" }}>
                        Order ID / Invoice #
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: "monospace" }}>
                        {selectedRecord.orderId}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 1.2 }}>
                      <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b" }}>
                        Purchased From
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedRecord.purchasedFrom}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b" }}>
                        Date of Purchase
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedRecord.dateOfPurchase
                          ? new Date(selectedRecord.dateOfPurchase).toLocaleDateString("en-IN", {
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                            })
                          : "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Right: Proof of Purchase / Invoice Viewer */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5, color: isDark ? "#cbd5e1" : "#334155" }}>
                    Proof of Purchase / Invoice
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2.5,
                      bgcolor: isDark ? "#1e293b" : "#f8fafc",
                      border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: 280,
                    }}
                  >
                    {selectedRecord.invoiceUrl ? (
                      (() => {
                        const url = getInvoiceFullUrl(selectedRecord.invoiceUrl);
                        const isPdf =
                          selectedRecord.invoiceMimeType === "application/pdf" ||
                          selectedRecord.invoiceUrl.toLowerCase().endsWith(".pdf") ||
                          selectedRecord.invoiceOriginalName?.toLowerCase().endsWith(".pdf");

                        return (
                          <Box sx={{ width: "100%", textAlign: "center" }}>
                            {isPdf ? (
                              <Box sx={{ p: 3, textAlign: "center" }}>
                                <FileText size={56} color="#ef4444" style={{ margin: "0 auto 12px" }} />
                                <Typography variant="body1" sx={{ fontWeight: 700, mb: 0.5 }}>
                                  PDF Document Attached
                                </Typography>
                                <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b", display: "block", mb: 2 }}>
                                  {selectedRecord.invoiceOriginalName || "Invoice_Proof.pdf"}
                                </Typography>
                                <Button
                                  variant="contained"
                                  startIcon={<ExternalLink size={16} />}
                                  component="a"
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{
                                    borderRadius: 2,
                                    textTransform: "none",
                                    fontWeight: 600,
                                    background: "linear-gradient(135deg, #ef4444, #dc2626)",
                                    color: "#fff",
                                  }}
                                >
                                  Open PDF Invoice in New Tab
                                </Button>
                              </Box>
                            ) : (
                              <Box>
                                <Box
                                  component="img"
                                  src={url}
                                  alt="Proof of purchase"
                                  sx={{
                                    maxHeight: 240,
                                    maxWidth: "100%",
                                    borderRadius: 2,
                                    objectFit: "contain",
                                    bgcolor: "#000",
                                    p: 0.5,
                                    mb: 1.5,
                                  }}
                                  onError={(e: any) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<ExternalLink size={14} />}
                                  component="a"
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{ borderRadius: 2, textTransform: "none" }}
                                >
                                  View Full Resolution
                                </Button>
                              </Box>
                            )}
                          </Box>
                        );
                      })()
                    ) : (
                      <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                        No invoice file was uploaded.
                      </Typography>
                    )}
                  </Box>

                  {/* Quick Verification Actions inside Modal */}
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1, color: isDark ? "#cbd5e1" : "#334155" }}>
                      Update Verification Status
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        color="success"
                        startIcon={<CheckCircle2 size={16} />}
                        onClick={() => handleStatusChange(selectedRecord, "verified")}
                        disabled={selectedRecord.status === "verified"}
                        sx={{
                          borderRadius: 2.5,
                          textTransform: "none",
                          fontWeight: 700,
                          bgcolor: selectedRecord.status === "verified" ? "rgba(22,163,74,0.1)" : "transparent",
                        }}
                      >
                        Verify Warranty
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        color="error"
                        startIcon={<XCircle size={16} />}
                        onClick={() => handleStatusChange(selectedRecord, "rejected")}
                        disabled={selectedRecord.status === "rejected"}
                        sx={{
                          borderRadius: 2.5,
                          textTransform: "none",
                          fontWeight: 700,
                          bgcolor: selectedRecord.status === "rejected" ? "rgba(220,38,38,0.1)" : "transparent",
                        }}
                      >
                        Reject Claim
                      </Button>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}` }}>
              <Button
                variant="outlined"
                color="error"
                startIcon={<Trash2 size={16} />}
                onClick={() => {
                  setRecordToDelete(selectedRecord);
                  setShowDeleteModal(true);
                }}
                sx={{ borderRadius: 2, textTransform: "none", mr: "auto" }}
              >
                Delete Record
              </Button>
              <Button
                variant="contained"
                onClick={() => setShowDetailsModal(false)}
                sx={{ borderRadius: 2, textTransform: "none", px: 3 }}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: isDark ? "#0f172a" : "#ffffff",
          },
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, color: "#ef4444" }}>
          <AlertTriangle size={24} />
          Delete Registration?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: isDark ? "#cbd5e1" : "#475569" }}>
            Are you sure you want to delete warranty registration{" "}
            <strong>{recordToDelete?.registrationNumber || recordToDelete?.orderId}</strong> for customer{" "}
            <strong>{recordToDelete?.customerName}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setShowDeleteModal(false)}
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={deletingId !== null}
            onClick={handleDeleteRecord}
            sx={{ borderRadius: 2, textTransform: "none", px: 2.5 }}
          >
            {deletingId ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%", borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WarrantyRegistrations;
